import React from 'react';
import { toast } from 'sonner';
import { Plug, RefreshCw, AlertTriangle, CheckCircle2, Activity, Play } from 'lucide-react';
import { Database, Phone, MessageCircle, MessageSquare, Mail, Shield, Fingerprint, CreditCard, Gavel, BarChart3 } from 'lucide-react';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useApp } from '@/context/AppContext';
import { formatNumber } from '@/lib/format';
import { usePageTitle } from '@/lib/usePageTitle';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/components/shared/ConfirmDialog';

const ICONS = { database: Database, phone: Phone, 'message-circle': MessageCircle, 'message-square': MessageSquare, mail: Mail, shield: Shield, fingerprint: Fingerprint, 'credit-card': CreditCard, gavel: Gavel, 'bar-chart-3': BarChart3 };

// Connectors we consider "core" — failure here visibly impacts collection ops.
const CORE_CONNECTORS = new Set(['SADAD', 'WhatsApp Business Cloud', 'Telephony', 'Nafath', 'Client ERP']);

export default function IntegrationHub() {
  usePageTitle('Integration Hub');
  const { integrations, toggleIntegration, pushEvent, events } = useApp();
  const confirm = useConfirm();

  const test = (it) => {
    toast.message(`Testing ${it.name}…`);
    pushEvent({ type: 'ConnectorTest', src: 'Admin Console', dst: it.name, status: it.status === 'connected' ? 'success' : 'failed', payload: `latency ${it.latency}ms`, caseId: '—' });
    setTimeout(() => toast.success(`${it.name} responded`, { description: `Latency ${it.latency}ms • ${it.status}` }), 500);
  };

  const testAll = () => {
    toast.message('Running health check on all connectors…');
    integrations.forEach((it, i) => setTimeout(() => test(it), i * 220));
  };

  const simulateFailure = async (it) => {
    const core = CORE_CONNECTORS.has(it.name);
    const ok = await confirm({
      title: `Simulate ${it.name} failure?`,
      description: core
        ? `${it.name} is a CORE connector. A simulated outage will visibly disrupt downstream flows (call attempts, payment links, debtor verifications). Use only for chaos drills.`
        : `${it.name} will be marked as failed in the integration table and downstream events will be queued.`,
      impactSummary: [
        { label: 'Connector', value: it.name },
        { label: 'Tier', value: core ? 'Core' : 'Optional' },
        { label: 'Currently', value: it.status },
        { label: 'Reversible', value: 'Yes — click again to restore' },
      ],
      confirmLabel: 'Simulate failure',
      tone: core ? 'destructive' : 'warning',
    });
    if (!ok) return;
    toggleIntegration(it.id);
    toast.message(`${it.name} — failure simulated`);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader
        eyebrow="Admin • IT"
        title="Integration Hub"
        subtitle="Visible mock connectors to Saudi financial ecosystem and operational systems — ERP, telephony, WhatsApp, SADAD, SIMAH, Nafath, Najiz and BI."
        actions={(
          <>
            <Button variant="outline" size="sm" onClick={testAll} data-testid="integration-test-all"><Activity className="h-4 w-4 mr-1.5" aria-hidden="true" />Test all</Button>
            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90"><Plug className="h-4 w-4 mr-1.5" aria-hidden="true" />Add connector</Button>
          </>
        )}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { l: 'Connected', v: integrations.filter((i) => i.status === 'connected').length, t: 'success' },
          { l: 'Degraded', v: integrations.filter((i) => i.status === 'degraded').length, t: 'warning' },
          { l: 'Failed', v: integrations.filter((i) => i.status === 'failed').length, t: 'destructive' },
          { l: 'Events / hour', v: 1247, t: 'info' },
        ].map((s) => (
          <div key={s.l} className="surface-card p-4">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.l}</p>
            <p className={`font-mono-num text-2xl font-semibold mt-1 ${s.t === 'success' ? 'text-success' : s.t === 'warning' ? 'text-warning' : s.t === 'destructive' ? 'text-destructive' : 'text-info'}`}>{formatNumber(s.v)}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {integrations.map((it) => {
          const Icon = ICONS[it.icon] || Plug;
          const tone = it.status === 'connected' ? 'success' : it.status === 'degraded' ? 'warning' : 'destructive';
          return (
            <div key={it.id} className="surface-card p-5 flex flex-col gap-3 hover:shadow-lift transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 border border-primary/30 text-primary flex items-center justify-center"><Icon className="h-4.5 w-4.5" aria-hidden="true" /></div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold">{it.name}</p>
                      <Badge variant="outline" className={cn(
                        'text-[9px] px-1.5 py-0 h-4',
                        CORE_CONNECTORS.has(it.name)
                          ? 'border-primary/40 bg-primary/10 text-gold-deep'
                          : 'border-border bg-muted text-muted-foreground'
                      )}>
                        {CORE_CONNECTORS.has(it.name) ? 'Core' : 'Optional'}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground">{it.vendor}</p>
                  </div>
                </div>
                <Badge variant="outline" className={
                  tone === 'success' ? 'bg-success/10 text-success border-success/40' :
                  tone === 'warning' ? 'bg-warning/10 text-warning border-warning/40' :
                  'bg-destructive/10 text-destructive border-destructive/30'
                }>
                  <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${tone === 'success' ? 'bg-success' : tone === 'warning' ? 'bg-warning' : 'bg-destructive'}`} />
                  {it.status}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="px-2.5 py-1.5 rounded-md bg-muted text-muted-foreground">Latency <span className="font-mono-num text-foreground ml-1">{it.latency}ms</span></div>
                <div className="px-2.5 py-1.5 rounded-md bg-muted text-muted-foreground">Last event <span className="text-foreground ml-1">{it.lastEvent}</span></div>
              </div>
              <div className="flex items-center gap-2 mt-auto pt-2 border-t border-border">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => test(it)} data-testid={`integration-test-${it.id}`}><Play className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />Test</Button>
                <Button size="sm" variant="ghost" className="flex-1 text-xs" onClick={() => simulateFailure(it)} data-testid={`integration-fail-${it.id}`}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />Simulate failure
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Event stream */}
      <div className="surface-card overflow-hidden">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-primary">Integration event log</p>
            <h3 className="text-base font-semibold mt-0.5">Real-time mock event stream</h3>
          </div>
          <Badge variant="outline" className="text-[10px]">{events.length} events buffered</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-mono-num">
            <thead className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-wider font-sans">
              <tr>
                <th scope="col" className="text-left px-5 py-2.5 font-medium">Event ID</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Type</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Source → Destination</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Payload</th>
                <th scope="col" className="text-right px-3 py-2.5 font-medium">Latency</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {events.slice(0, 14).map((e) => (
                <tr key={e.eventId} className="border-t border-border hover:bg-muted/40">
                  <td className="px-5 py-2 text-xs text-muted-foreground">{e.eventId}</td>
                  <td className="px-3 py-2 text-xs">{e.type}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{e.src} <span className="mx-1">→</span> {e.dst}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-md">{e.payload}</td>
                  <td className="px-3 py-2 text-xs text-right">{e.latencyMs}ms</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${e.status === 'success' ? 'bg-success/10 text-success' : e.status === 'failed' ? 'bg-destructive/10 text-destructive' : 'bg-warning/10 text-warning'}`}>
                      {e.status === 'success' ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                      {e.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
